import React, { useState, useEffect, useCallback } from 'react';
import {
    Shield,
    Search,
    Clock,
    Info,
    AlertTriangle,
    Lock,
    Eye,
    ChevronLeft,
    ChevronRight,
    Activity,
    DollarSign,
    Users,
    FileText,
    Bell,
    Key,
    Layers,
    TrendingUp,
    Calendar,
    Filter,
    XCircle
} from 'lucide-react';
import {
    Card,
    Badge,
    Modal,
    Input,
    Spinner,
    ButtonSecondary
} from '../../components/ComponentLibrary';
import { getAuditLogs, getAuditStats } from '../../services/api';
import { useAuth } from '../../services/AuthContext';
import './Admin.css';

/**
 * Admin Audit Logs Page
 * 
 * Read-only, immutable system audit trail.
 * Visible only to Mohamed & Abdallah.
 * Cannot be edited or deleted.
 */
export default function AdminAuditLogs() {
    const { user } = useAuth();
    const [logs, setLogs] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [statsLoading, setStatsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedLog, setSelectedLog] = useState(null);
    const [pagination, setPagination] = useState({ total: 0, page: 1, pages: 1, limit: 30 });

    // Filters
    const [filters, setFilters] = useState({
        adminName: '',
        category: '',
        severity: '',
        startDate: '',
        endDate: '',
        search: ''
    });

    const [searchInput, setSearchInput] = useState('');

    const fetchLogs = useCallback(async () => {
        try {
            setLoading(true);
            const cleanFilters = {};
            Object.entries(filters).forEach(([key, val]) => {
                if (val) cleanFilters[key] = val;
            });
            cleanFilters.page = pagination.page;
            cleanFilters.limit = pagination.limit;

            const response = await getAuditLogs(cleanFilters);
            setLogs(response.data || []);
            if (response.pagination) {
                setPagination(prev => ({ ...prev, ...response.pagination }));
            }
            setError(null);
        } catch (err) {
            setError('Failed to load audit logs. Access may be restricted.');
        } finally {
            setLoading(false);
        }
    }, [filters, pagination.page, pagination.limit]);

    const fetchStats = useCallback(async () => {
        try {
            setStatsLoading(true);
            const data = await getAuditStats();
            setStats(data);
        } catch (err) {
            // Stats failure is non-critical
        } finally {
            setStatsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchLogs();
    }, [fetchLogs]);

    useEffect(() => {
        fetchStats();
    }, [fetchStats]);

    // Debounced search
    useEffect(() => {
        const timer = setTimeout(() => {
            if (searchInput !== filters.search) {
                setFilters(prev => ({ ...prev, search: searchInput }));
                setPagination(prev => ({ ...prev, page: 1 }));
            }
        }, 400);
        return () => clearTimeout(timer);
    }, [searchInput]);

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const clearFilters = () => {
        setFilters({
            adminName: '',
            category: '',
            severity: '',
            startDate: '',
            endDate: '',
            search: ''
        });
        setSearchInput('');
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const hasActiveFilters = Object.values(filters).some(v => v !== '');

    // ==================== HELPERS ====================

    const getCategoryIcon = (category) => {
        const icons = {
            'FINANCIAL': <DollarSign size={14} />,
            'STUDENT_MGMT': <Users size={14} />,
            'QUIZ_MGMT': <FileText size={14} />,
            'SYSTEM': <Layers size={14} />,
            'PAYROLL': <DollarSign size={14} />,
            'NOTIFICATION': <Bell size={14} />,
            'AUTH': <Key size={14} />,
            'GROUP_MGMT': <Users size={14} />
        };
        return icons[category] || <Activity size={14} />;
    };

    const getCategoryColor = (category) => {
        const colors = {
            'FINANCIAL': '#10b981',
            'STUDENT_MGMT': '#3b82f6',
            'QUIZ_MGMT': '#8b5cf6',
            'SYSTEM': '#f59e0b',
            'PAYROLL': '#06b6d4',
            'NOTIFICATION': '#ec4899',
            'AUTH': '#ef4444',
            'GROUP_MGMT': '#14b8a6'
        };
        return colors[category] || '#64748b';
    };

    const getSeverityBadge = (severity) => {
        const map = {
            'LOW': { variant: 'ocean', label: 'Low' },
            'MEDIUM': { variant: 'warning', label: 'Medium' },
            'HIGH': { variant: 'error', label: 'High' },
            'CRITICAL': { variant: 'error', label: '🔴 Critical' }
        };
        return map[severity] || map['MEDIUM'];
    };

    const formatDate = (dateStr) => {
        const d = new Date(dateStr);
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };

    const formatTime = (dateStr) => {
        const d = new Date(dateStr);
        return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    };

    // ==================== RENDER ====================

    return (
        <div className="admin-audit-logs-page">
            {/* Page Header */}
            <header className="admin-page-header">
                <div className="admin-page-header-text">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <div style={{
                            width: '48px', height: '48px', borderRadius: '14px',
                            background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            boxShadow: '0 4px 14px rgba(15, 23, 42, 0.3)'
                        }}>
                            <Shield size={24} color="#38bdf8" />
                        </div>
                        <div>
                            <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 900, color: '#0f172a', letterSpacing: '-0.03em' }}>
                                System Audit Logs
                            </h1>
                            <p style={{ margin: 0, fontSize: '0.8rem', color: '#94a3b8', fontWeight: 600 }}>
                                Immutable record of all administrative actions • Read-Only
                            </p>
                        </div>
                    </div>
                    <div style={{
                        display: 'flex', alignItems: 'center', gap: '0.5rem',
                        background: '#fef3c7', padding: '0.5rem 1rem', borderRadius: '10px',
                        border: '1px solid #fcd34d', marginTop: '0.75rem'
                    }}>
                        <Lock size={14} color="#d97706" />
                        <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#92400e' }}>
                            Access restricted to {user?.fullName || 'Authorized Admins'} • Logs cannot be edited or deleted
                        </span>
                    </div>
                </div>
            </header>

            {/* Stats Overview */}
            {!statsLoading && stats && (
                <div className="audit-stats-grid">
                    <div className="audit-stat-card">
                        <div className="audit-stat-icon" style={{ background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)' }}>
                            <Activity size={20} color="white" />
                        </div>
                        <div className="audit-stat-info">
                            <span className="audit-stat-value">{stats.total?.toLocaleString() || 0}</span>
                            <span className="audit-stat-label">Total Entries</span>
                        </div>
                    </div>
                    <div className="audit-stat-card">
                        <div className="audit-stat-icon" style={{ background: 'linear-gradient(135deg, #10b981, #059669)' }}>
                            <TrendingUp size={20} color="white" />
                        </div>
                        <div className="audit-stat-info">
                            <span className="audit-stat-value">{stats.today || 0}</span>
                            <span className="audit-stat-label">Today</span>
                        </div>
                    </div>
                    <div className="audit-stat-card">
                        <div className="audit-stat-icon" style={{ background: 'linear-gradient(135deg, #f59e0b, #d97706)' }}>
                            <Calendar size={20} color="white" />
                        </div>
                        <div className="audit-stat-info">
                            <span className="audit-stat-value">{stats.thisWeek || 0}</span>
                            <span className="audit-stat-label">This Week</span>
                        </div>
                    </div>
                    <div className="audit-stat-card">
                        <div className="audit-stat-icon" style={{ background: 'linear-gradient(135deg, #ef4444, #dc2626)' }}>
                            <AlertTriangle size={20} color="white" />
                        </div>
                        <div className="audit-stat-info">
                            <span className="audit-stat-value">{stats.critical || 0}</span>
                            <span className="audit-stat-label">Critical</span>
                        </div>
                    </div>
                </div>
            )}

            {/* Filters */}
            <Card className="filter-card mb-4" hoverable={false}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                    <Filter size={16} color="#64748b" />
                    <span style={{ fontSize: '0.85rem', fontWeight: 700, color: '#475569' }}>Filter Audit Trail</span>
                    {hasActiveFilters && (
                        <button
                            onClick={clearFilters}
                            style={{
                                marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '0.35rem',
                                padding: '0.35rem 0.75rem', borderRadius: '8px', border: '1px solid #fecaca',
                                background: '#fef2f2', color: '#dc2626', fontSize: '0.75rem', fontWeight: 700,
                                cursor: 'pointer'
                            }}
                        >
                            <XCircle size={12} /> Clear Filters
                        </button>
                    )}
                </div>
                <div className="filter-grid-audit">
                    <div className="filter-col">
                        <label className="cw-input-label">Search</label>
                        <div style={{ position: 'relative' }}>
                            <Search size={14} style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                            <input
                                className="cw-input"
                                style={{ paddingLeft: '2.25rem' }}
                                placeholder="Search descriptions..."
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                            />
                        </div>
                    </div>
                    <div className="filter-col">
                        <label className="cw-input-label">Admin</label>
                        <select
                            className="cw-input"
                            value={filters.adminName}
                            onChange={(e) => handleFilterChange('adminName', e.target.value)}
                        >
                            <option value="">All Admins</option>
                            <option value="Mohamed">Mohamed</option>
                            <option value="Abdallah">Abdallah</option>
                        </select>
                    </div>
                    <div className="filter-col">
                        <label className="cw-input-label">Category</label>
                        <select
                            className="cw-input"
                            value={filters.category}
                            onChange={(e) => handleFilterChange('category', e.target.value)}
                        >
                            <option value="">All Categories</option>
                            <option value="FINANCIAL">Financial</option>
                            <option value="STUDENT_MGMT">Student Management</option>
                            <option value="QUIZ_MGMT">Quiz Management</option>
                            <option value="SYSTEM">System</option>
                            <option value="PAYROLL">Payroll</option>
                            <option value="NOTIFICATION">Notifications</option>
                            <option value="AUTH">Authentication</option>
                            <option value="GROUP_MGMT">Group Management</option>
                        </select>
                    </div>
                    <div className="filter-col">
                        <label className="cw-input-label">Severity</label>
                        <select
                            className="cw-input"
                            value={filters.severity}
                            onChange={(e) => handleFilterChange('severity', e.target.value)}
                        >
                            <option value="">All Levels</option>
                            <option value="LOW">Low</option>
                            <option value="MEDIUM">Medium</option>
                            <option value="HIGH">High</option>
                            <option value="CRITICAL">Critical</option>
                        </select>
                    </div>
                    <div className="filter-col">
                        <label className="cw-input-label">From</label>
                        <Input
                            type="date"
                            value={filters.startDate}
                            onChange={(e) => handleFilterChange('startDate', e.target.value)}
                        />
                    </div>
                    <div className="filter-col">
                        <label className="cw-input-label">To</label>
                        <Input
                            type="date"
                            value={filters.endDate}
                            onChange={(e) => handleFilterChange('endDate', e.target.value)}
                        />
                    </div>
                </div>
            </Card>

            {/* Logs Table */}
            <div className="admin-table-section-card">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1rem 1.5rem', borderBottom: '1px solid #f1f5f9' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <Eye size={16} color="#64748b" />
                        <span style={{ fontSize: '0.85rem', fontWeight: 700, color: '#475569' }}>
                            Audit Trail ({pagination.total} records)
                        </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <Lock size={12} color="#94a3b8" />
                        <span style={{ fontSize: '0.7rem', fontWeight: 600, color: '#94a3b8' }}>READ-ONLY</span>
                    </div>
                </div>

                {loading ? (
                    <div className="loading-container p-5 text-center">
                        <Spinner size="lg" />
                        <p className="mt-2 text-muted">Decrypting audit trail...</p>
                    </div>
                ) : error ? (
                    <div style={{ padding: '3rem', textAlign: 'center' }}>
                        <Shield size={48} color="#dc2626" style={{ opacity: 0.3, marginBottom: '1rem' }} />
                        <p style={{ color: '#dc2626', fontWeight: 600 }}>{error}</p>
                    </div>
                ) : logs.length === 0 ? (
                    <div style={{ padding: '3rem', textAlign: 'center' }}>
                        <Shield size={48} color="#94a3b8" style={{ opacity: 0.2, marginBottom: '1rem' }} />
                        <p style={{ color: '#94a3b8', fontWeight: 600 }}>No audit entries found matching your filters.</p>
                    </div>
                ) : (
                    <div className="admin-table-wrapper">
                        <table className="admin-standard-table">
                            <thead>
                                <tr>
                                    <th style={{ width: '140px' }}>Timestamp</th>
                                    <th style={{ width: '110px' }}>Admin</th>
                                    <th style={{ width: '130px' }}>Category</th>
                                    <th style={{ width: '130px' }}>Action</th>
                                    <th>Description</th>
                                    <th style={{ width: '90px' }}>Severity</th>
                                    <th style={{ width: '70px', textAlign: 'right' }}>View</th>
                                </tr>
                            </thead>
                            <tbody>
                                {logs.map(log => {
                                    const severityInfo = getSeverityBadge(log.severity);
                                    return (
                                        <tr key={log._id} className={log.severity === 'CRITICAL' ? 'audit-row-critical' : ''}>
                                            <td>
                                                <div className="time-col">
                                                    <div className="date-txt">{formatDate(log.createdAt)}</div>
                                                    <div className="time-txt">
                                                        <Clock size={11} /> {formatTime(log.createdAt)}
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="admin-cell">
                                                    <div className="admin-avatar-xs" style={{
                                                        background: log.adminName === 'Mohamed'
                                                            ? 'linear-gradient(135deg, #3b82f6, #1d4ed8)'
                                                            : 'linear-gradient(135deg, #8b5cf6, #6d28d9)'
                                                    }}>
                                                        {log.adminName[0]}
                                                    </div>
                                                    <span style={{ fontWeight: 700, fontSize: '0.8rem' }}>{log.adminName}</span>
                                                </div>
                                            </td>
                                            <td>
                                                <div style={{
                                                    display: 'inline-flex', alignItems: 'center', gap: '0.35rem',
                                                    padding: '0.3rem 0.65rem', borderRadius: '8px',
                                                    background: `${getCategoryColor(log.category)}12`,
                                                    color: getCategoryColor(log.category),
                                                    fontSize: '0.72rem', fontWeight: 700
                                                }}>
                                                    {getCategoryIcon(log.category)}
                                                    {log.category?.replace(/_/g, ' ')}
                                                </div>
                                            </td>
                                            <td>
                                                <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#334155' }}>
                                                    {log.actionType?.replace(/_/g, ' ')}
                                                </span>
                                            </td>
                                            <td>
                                                <div className="description-cell" title={log.description} style={{ maxWidth: '300px' }}>
                                                    {log.description}
                                                </div>
                                            </td>
                                            <td>
                                                <Badge variant={severityInfo.variant}>
                                                    {severityInfo.label}
                                                </Badge>
                                            </td>
                                            <td>
                                                <div className="action-cell-right">
                                                    <button
                                                        className="btn-action-sm"
                                                        onClick={() => setSelectedLog(log)}
                                                        title="View Details"
                                                    >
                                                        <Info size={14} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Pagination */}
                {pagination.pages > 1 && (
                    <div className="audit-pagination">
                        <button
                            className="audit-page-btn"
                            disabled={pagination.page <= 1}
                            onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                        >
                            <ChevronLeft size={16} />
                        </button>
                        <span className="audit-page-info">
                            Page {pagination.page} of {pagination.pages}
                        </span>
                        <button
                            className="audit-page-btn"
                            disabled={pagination.page >= pagination.pages}
                            onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                        >
                            <ChevronRight size={16} />
                        </button>
                    </div>
                )}
            </div>

            {/* Detail Modal */}
            <Modal
                isOpen={!!selectedLog}
                onClose={() => setSelectedLog(null)}
                title="Audit Entry Details"
                size="lg"
            >
                {selectedLog && (
                    <div className="audit-detail-modal">
                        {/* Header */}
                        <div style={{
                            background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
                            borderRadius: '14px', padding: '1.5rem', marginBottom: '1.5rem',
                            color: 'white'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
                                <Shield size={20} color="#38bdf8" />
                                <Badge variant={getSeverityBadge(selectedLog.severity).variant}>
                                    {getSeverityBadge(selectedLog.severity).label}
                                </Badge>
                                <div style={{
                                    display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
                                    padding: '0.25rem 0.6rem', borderRadius: '6px',
                                    background: `${getCategoryColor(selectedLog.category)}30`,
                                    color: getCategoryColor(selectedLog.category),
                                    fontSize: '0.7rem', fontWeight: 700
                                }}>
                                    {getCategoryIcon(selectedLog.category)}
                                    {selectedLog.category?.replace(/_/g, ' ')}
                                </div>
                            </div>
                            <h2 style={{ fontSize: '1.1rem', fontWeight: 800, margin: '0 0 0.35rem 0', lineHeight: 1.4 }}>
                                {selectedLog.description}
                            </h2>
                            <div style={{ fontSize: '0.75rem', color: '#94a3b8', fontWeight: 600 }}>
                                {selectedLog.actionType?.replace(/_/g, ' ')}
                            </div>
                        </div>

                        {/* Meta Grid */}
                        <div className="audit-meta-grid">
                            <div className="audit-meta-item">
                                <span className="audit-meta-label">Performed By</span>
                                <span className="audit-meta-value">
                                    <div className="admin-avatar-xs" style={{
                                        background: selectedLog.adminName === 'Mohamed'
                                            ? 'linear-gradient(135deg, #3b82f6, #1d4ed8)'
                                            : 'linear-gradient(135deg, #8b5cf6, #6d28d9)',
                                        display: 'inline-flex', marginRight: '0.5rem'
                                    }}>
                                        {selectedLog.adminName[0]}
                                    </div>
                                    {selectedLog.adminName}
                                </span>
                            </div>
                            <div className="audit-meta-item">
                                <span className="audit-meta-label">Date & Time</span>
                                <span className="audit-meta-value">
                                    {new Date(selectedLog.createdAt).toLocaleString()}
                                </span>
                            </div>
                            {selectedLog.targetEntity?.type && (
                                <div className="audit-meta-item">
                                    <span className="audit-meta-label">Target Entity</span>
                                    <span className="audit-meta-value">
                                        {selectedLog.targetEntity.type}
                                        {selectedLog.targetEntity.name && ` — ${selectedLog.targetEntity.name}`}
                                    </span>
                                </div>
                            )}
                            {selectedLog.metadata?.amount && (
                                <div className="audit-meta-item">
                                    <span className="audit-meta-label">Amount</span>
                                    <span className="audit-meta-value" style={{ color: '#10b981', fontWeight: 800 }}>
                                        {selectedLog.metadata.amount?.toLocaleString()} {selectedLog.metadata.currency || 'EGP'}
                                    </span>
                                </div>
                            )}
                        </div>

                        {/* State Comparison */}
                        {(selectedLog.previousState || selectedLog.newState) && (
                            <div className="audit-state-section">
                                {selectedLog.previousState && (
                                    <div className="audit-state-box previous">
                                        <h4><AlertTriangle size={14} /> Previous State</h4>
                                        <pre>{JSON.stringify(selectedLog.previousState, null, 2)}</pre>
                                    </div>
                                )}
                                {selectedLog.newState && (
                                    <div className="audit-state-box current">
                                        <h4><Shield size={14} /> New State</h4>
                                        <pre>{JSON.stringify(selectedLog.newState, null, 2)}</pre>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Immutability Notice */}
                        <div style={{
                            display: 'flex', alignItems: 'center', gap: '0.5rem',
                            background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '10px',
                            padding: '0.75rem 1rem', marginTop: '1.5rem'
                        }}>
                            <Lock size={14} color="#16a34a" />
                            <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#15803d' }}>
                                This audit entry is cryptographically sealed and cannot be modified or deleted.
                            </span>
                        </div>

                        <div className="modal-footer-actions" style={{ marginTop: '1.5rem' }}>
                            <ButtonSecondary onClick={() => setSelectedLog(null)}>Close</ButtonSecondary>
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
}
